import { useCallback, useEffect, useState } from 'react';
import { creationRequestsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

export default function useCreationRequestQueue() {
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [approvedSecret, setApprovedSecret] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await creationRequestsAPI.list({ status, limit: 80 });
      const data = res.data?.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load account requests'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = items.find((row) => row.id === selectedId) || null;

  const approve = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    setError('');
    setMessage('');
    setApprovedSecret(null);
    try {
      const res = await creationRequestsAPI.approve(selectedId, { reviewNote });
      const data = res.data?.data;
      setApprovedSecret(data?.temporaryPassword || null);
      setMessage(
        `Account created for ${data?.user?.email || 'the requester'}. Share the temporary password securely.`
      );
      setReviewNote('');
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not approve this request'));
    } finally {
      setBusy(false);
    }
  }, [load, reviewNote, selectedId]);

  const reject = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await creationRequestsAPI.reject(selectedId, { reviewNote });
      setMessage('Request rejected.');
      setReviewNote('');
      setSelectedId(null);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not reject this request'));
    } finally {
      setBusy(false);
    }
  }, [load, reviewNote, selectedId]);

  return {
    status,
    setStatus,
    items,
    total,
    selectedId,
    setSelectedId,
    selected,
    loading,
    busy,
    error,
    message,
    reviewNote,
    setReviewNote,
    approvedSecret,
    approve,
    reject,
    load,
  };
}
